import {
  computed, onMounted, ref, watch,
} from "@vue/composition-api";
import {useToast} from "vue-toastification/composition";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
// import {useUtils as useI18nUtils} from "@core/libs/i18n";
import store from "@/store";
import {banIp, listBlockedIps, unbanIp} from "./api";

const getErrorMessage = (error, fallback) => error?.response?.data?.message ?? fallback;

export default function useIpBlockList() {
  const toast = useToast();
  // const {t} = useI18nUtils(); // TODO: Fix translation helper
  const selectedAccount = ref(null);
  const blockedIps = ref([]);
  const selectedIps = ref([]);
  const newIp = ref("");
  const isLoadingAccounts = ref(false);
  const isLoading = ref(false);
  const isMutating = ref(false);
  const activeIp = ref(null);
  let listRequestId = 0;

  const masterAccountOptions = computed(
    () => store.getters["pbx_master_account/getSelectedItems"],
  );
  const selectedDomain = computed(() => selectedAccount.value?.domain ?? "");
  const allSelected = computed({
    get: () => blockedIps.value.length > 0
      && selectedIps.value.length === blockedIps.value.length,
    set: (checked) => {
      selectedIps.value = checked ? [...blockedIps.value] : [];
    },
  });
  const selectionIndeterminate = computed(() => selectedIps.value.length > 0
    && selectedIps.value.length < blockedIps.value.length);

  const notify = (title, variant = "danger") => {
    toast({
      component: ToastificationContent,
      props: {
        title,
        icon: variant === "success" ? "CheckIcon" : "AlertTriangleIcon",
        variant,
      },
    }, {timeout: variant === "danger" ? 10000 : 5000});
  };

  const fetchBlockedIps = async () => {
    const domain = selectedDomain.value;
    const requestId = ++listRequestId;

    if (!domain) {
      blockedIps.value = [];
      isLoading.value = false;
      return;
    }

    isLoading.value = true;
    try {
      const ips = await listBlockedIps(domain);
      if (requestId === listRequestId) {
        blockedIps.value = ips;
        selectedIps.value = selectedIps.value.filter((ip) => ips.includes(ip));
      }
    } catch (error) {
      if (requestId === listRequestId) {
        blockedIps.value = [];
        selectedIps.value = [];
        notify(getErrorMessage(error, 'Ocurrió un error'));
      }
    } finally {
      if (requestId === listRequestId) {
        isLoading.value = false;
      }
    }
  };

  const loadMasterAccounts = async () => {
    isLoadingAccounts.value = true;
    try {
      await store.dispatch("pbx_master_account/getAll");
      selectedAccount.value = masterAccountOptions.value[0] ?? null;
    } catch (error) {
      notify(getErrorMessage(error, 'Ocurrió un error'));
    } finally {
      isLoadingAccounts.value = false;
    }
  };

  const blockIp = async () => {
    const ip = newIp.value.trim();
    if (!selectedDomain.value || !ip || isMutating.value) return false;

    isMutating.value = true;
    activeIp.value = ip;
    try {
      await banIp(selectedDomain.value, ip);
      newIp.value = "";
      notify(`IP ${ip} bloqueada correctamente`, "success");
      await fetchBlockedIps();
      return true;
    } catch (error) {
      notify(getErrorMessage(error, 'Ocurrió un error'));
      return false;
    } finally {
      activeIp.value = null;
      isMutating.value = false;
    }
  };

  const unblockIp = async (ip) => {
    if (!selectedDomain.value || !ip || isMutating.value) return;

    isMutating.value = true;
    activeIp.value = ip;
    try {
      await unbanIp(selectedDomain.value, ip);
      selectedIps.value = selectedIps.value.filter((selectedIp) => selectedIp !== ip);
      notify(`IP ${ip} desbloqueada correctamente`, "success");
      await fetchBlockedIps();
    } catch (error) {
      notify(getErrorMessage(error, 'Ocurrió un error'));
    } finally {
      activeIp.value = null;
      isMutating.value = false;
    }
  };

  const unblockSelected = async () => {
    if (!selectedDomain.value || !selectedIps.value.length || isMutating.value) return;

    const ips = [...selectedIps.value];
    let succeeded = 0;
    let failed = 0;
    isMutating.value = true;

    for (const ip of ips) {
      activeIp.value = ip;
      try {
        await unbanIp(selectedDomain.value, ip);
        succeeded += 1;
      } catch {
        failed += 1;
      }
    }

    activeIp.value = null;
    try {
      await fetchBlockedIps();
    } finally {
      isMutating.value = false;
    }

    if (succeeded) {
      notify(`${succeeded} IP desbloqueadas correctamente`, "success");
    }
    if (failed) {
      notify(`${failed} IP no pudieron ser desbloqueadas.`);
    }
  };

  watch(selectedAccount, () => {
    selectedIps.value = [];
    newIp.value = "";
    fetchBlockedIps();
  });

  onMounted(loadMasterAccounts);

  return {
    activeIp,
    allSelected,
    blockedIps,
    blockIp,
    fetchBlockedIps,
    isLoading,
    isLoadingAccounts,
    isMutating,
    masterAccountOptions,
    newIp,
    selectedAccount,
    selectedDomain,
    selectedIps,
    selectionIndeterminate,
    unblockIp,
    unblockSelected,
  };
}
